/* Display arithmetic for the versioned monthly policy. The server validates and recomputes on save. */
import { MonthlyBucket, MonthlyCharge, MonthlyRuleId, MonthlySimple } from './api';
import { monthlyCaseScopeLabel } from './monthlyCaseScope';

export const MONTHLY_POLICY = 'lato-monthly-v1' as const;
export const MONTHLY_RULES: Record<MonthlyRuleId, { bucket: MonthlyBucket; rate: number | null; unit: string; fixed?: boolean }> = {
  sms: { bucket: 'communication', rate: 3, unit: 'SMS contact / month' },
  email: { bucket: 'communication', rate: 1, unit: 'email contact / month' },
  sms_email: { bucket: 'communication', rate: 4, unit: 'active CRM lead / month' },
  voice: { bucket: 'communication', rate: 1, unit: '2-minute call' },
  mailbox: { bucket: 'processing', rate: 300, unit: 'mailbox / month' },
  processing_heavy: { bucket: 'processing', rate: 5, unit: 'active unit / month' },
  processing_light: { bucket: 'processing', rate: 2, unit: 'active unit / month' },
  generated_document: { bucket: 'processing', rate: 10, unit: 'generated document' },
  platform_500: { bucket: 'infrastructure', rate: 500, unit: 'month', fixed: true },
  platform_750: { bucket: 'infrastructure', rate: 750, unit: 'month', fixed: true },
  platform_850: { bucket: 'infrastructure', rate: 850, unit: 'month', fixed: true },
  hosting_500: { bucket: 'infrastructure', rate: 500, unit: 'month', fixed: true },
  hosting_750: { bucket: 'infrastructure', rate: 750, unit: 'month', fixed: true },
  ap_unlimited: { bucket: 'processing', rate: 1000, unit: 'month', fixed: true },
  secondary_unlimited: { bucket: 'processing', rate: 500, unit: 'month', fixed: true },
  payroll: { bucket: 'processing', rate: 250, unit: 'up to 50 employees / month' },
  third_party: { bucket: 'third_party', rate: null, unit: 'month' },
  third_party_record: { bucket: 'third_party', rate: null, unit: 'record' },
  phase_addon: { bucket: 'infrastructure', rate: null, unit: 'month', fixed: true },
  phase_user: { bucket: 'processing', rate: null, unit: 'additional user / month' },
};
const round = (n: number) => Math.round(n * 100) / 100;
export const monthlyUsd = (n: number) => '$' + n.toLocaleString('en-US', { maximumFractionDigits: 6 });
export function chargeRate(c: MonthlyCharge): number | null {
  if (c.ruleId === 'phase_addon' || c.ruleId === 'phase_user') return c.manualRate ?? null;
  if (c.ruleId === 'third_party_record') return c.unitCost == null ? null : c.unitCost * 1.2;
  if (c.ruleId === 'third_party') return c.unitCost ?? null;
  return MONTHLY_RULES[c.ruleId].rate;
}
export function chargeExample(c: MonthlyCharge): number | null {
  const rate = chargeRate(c);
  if (rate == null) return null;
  if (MONTHLY_RULES[c.ruleId].fixed || c.ruleId === 'third_party') return rate;
  if (c.ruleId === 'payroll') return c.quantity == null ? null : 250 + Math.ceil(Math.max(0, c.quantity - 50) / 5) * 25;
  if (c.quantity == null || (c.ruleId === 'voice' && (c.minimum == null || c.minimum <= 0))) return null;
  return round(rate * Math.max(c.quantity, c.minimum ?? 0));
}
export function chargeMinimum(c: MonthlyCharge): number | null {
  const rate = chargeRate(c);
  if (rate == null) return null;
  if (MONTHLY_RULES[c.ruleId].fixed || c.ruleId === 'third_party') return rate;
  if (c.ruleId === 'payroll') return 250;
  if (c.ruleId === 'voice' && (c.minimum == null || c.minimum <= 0)) return null;
  return round(rate * (c.minimum ?? 0));
}
function baseChargeTerms(c: MonthlyCharge): string {
  const rule = MONTHLY_RULES[c.ruleId];
  const rate = chargeRate(c);
  if (c.ruleId === 'third_party' || c.ruleId === 'third_party_record') {
    if (rate == null) return `${c.label}: at cost${c.ruleId === 'third_party_record' ? ' + 20%' : ''} (TBD; excluded)`;
    return `${c.label}: ${monthlyUsd(rate)} / ${c.ruleId === 'third_party_record' ? 'record' : 'month'}`;
  }
  if (rate == null) return `${c.label}: price to confirm`;
  if (c.ruleId === 'payroll') return `${monthlyUsd(250)} up to 50 employees; +$25 / extra 5`;
  if (rule.fixed) return `${c.label}: ${monthlyUsd(rate)} / month`;
  const unit = canonicalUnitLabel(c.ruleId, c.unitLabel) || rule.unit;
  const minimum = c.ruleId === 'voice' && (c.minimum == null || c.minimum <= 0) ? '; minimum to confirm' : (c.minimum ?? 0) > 0 ? `; min ${c.minimum}` : '';
  const unlimited = c.ruleId === 'sms' || c.ruleId === 'email' || c.ruleId === 'sms_email' ? ' (unlimited)' : '';
  return `${c.label}: ${monthlyUsd(rate)} / ${unit}${unlimited}${minimum}`;
}
export interface MonthlyTermsOptions { includeScope?: boolean; caseNames?: string[] }
/** Editors render scope beside the tariff; public readouts can request it here. */
export function chargeTerms(c: MonthlyCharge, options: MonthlyTermsOptions = {}): string {
  const terms = baseChargeTerms(c);
  const scope = options.includeScope ? monthlyCaseScopeLabel(c.caseNames ?? options.caseNames) : '';
  return scope ? `${terms} · ${scope}` : terms;
}
export function simpleMonthlyLines(simple: MonthlySimple, options: MonthlyTermsOptions = {}) {
  const terms = simple.charges.map(charge => chargeTerms(charge, options));
  const amounts = simple.charges.filter(c => MONTHLY_RULES[c.ruleId].bucket !== 'third_party' || chargeRate(c) != null).map(chargeMinimum);
  return {
    minimum_monthly: amounts.some(n => n == null) ? null : round(amounts.reduce<number>((sum, n) => sum + (n ?? 0), 0)),
    price: terms.join('; '), overage: null,
    sentence: terms.join('; '),
  };
}

const NATURAL_UNITS = new Set(['active group / month', 'active client job / month', 'active project / month', 'active user / month', 'active member / month', 'active lead / month', 'active proposal / month']);
export function canonicalUnitLabel(ruleId: MonthlyRuleId, value: unknown): string | undefined {
  if (ruleId !== 'processing_heavy' && ruleId !== 'processing_light') return undefined;
  const proposed = typeof value === 'string' ? value.toLowerCase().trim() : '';
  return NATURAL_UNITS.has(proposed) ? proposed : undefined;
}
